// src/components/financials/FinancialAuditLog.js
// Updated: Tuesday, August 4, 2026 at 8:25 PM EDT

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Paper, Stack, Typography,
} from '@mui/material';
import {
  collection, limit, onSnapshot, orderBy, query,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';

const formatTimestamp = (value) => {
  if (!value) return 'Pending server timestamp';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown date' : date.toLocaleString();
};

const FinancialAuditLog = () => {
  const { userProfile } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userProfile?.churchId) {
      setLoading(false);
      return undefined;
    }

    const auditRef = collection(
      db,
      'churches',
      userProfile.churchId,
      'financials',
      'data',
      'auditLog',
    );
    const auditQuery = query(auditRef, orderBy('timestamp', 'desc'), limit(100));

    return onSnapshot(
      auditQuery,
      (snapshot) => {
        setRecords(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoading(false);
      },
      (snapshotError) => {
        setError(snapshotError.message);
        setLoading(false);
      },
    );
  }, [userProfile?.churchId]);

  if (loading) return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!records.length) return <Alert severity="info">No financial audit activity has been recorded yet.</Alert>;

  return (
    <Stack spacing={1.5}>
      {records.map((record) => (
        <Paper key={record.id} variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight="bold">{record.action || 'FINANCIAL_ACTION'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {record.actorName || record.actorUid || 'System'} • {formatTimestamp(record.timestamp)}
              </Typography>
              {record.memo && <Typography variant="body2" sx={{ mt: 1 }}>{record.memo}</Typography>}
              {record.reason && <Typography variant="body2" sx={{ mt: 1 }}>Reason: {record.reason}</Typography>}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {record.entityType && <Chip size="small" label={record.entityType} />}
              {record.overrideUsed && <Chip size="small" color="warning" label="Override used" />}
            </Stack>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export default FinancialAuditLog;
